import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Swal from "sweetalert2";
import ActionsMenu from "./ActionsMenu";

const emptyForm = {
  fee_head_id: "",
  student_id: "",
  fee_amount: "",
  workflow_status: "DRAFT",
};

const pageSizeOptions = [10, 20, 50];
const workflowStatusOptions = [
  "DRAFT",
  "APPROVED",
  "ACTIVE",
  "INACTIVE",
  "PENDING",
  "COMPLETED",
  "CLOSED",
];

const emptyUploadForm = {
  academic_year: "",
  fee_type_id: "",
  fee_head_id: "",
};

const normalizeUploadKey = (key) =>
  String(key || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const getUploadValue = (row, keys) => {
  const normalized = Object.fromEntries(
    Object.entries(row || {}).map(([key, value]) => [normalizeUploadKey(key), value])
  );
  for (const key of keys) {
    const value = normalized[normalizeUploadKey(key)];
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
};

const mapFeeBookRow = (row) => ({
  ...row,
  id: row.fee_book_id,
  fee_head_name: row.feeHead?.fee_head_name || "",
  student_rollno: row.student_rollno || row.student?.student_rollno || "",
  workflow_status: row.workflow_status || "",
  created_on: row.created_on ? row.created_on.slice(0, 10) : "",
});

export default function FeeBook({ openUploadOnMount = false }) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const userEmail = user?.user_email || "";

  const [rows, setRows] = useState([]);
  const [feeHeads, setFeeHeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [editingFeeBookId, setEditingFeeBookId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [anchorEl, setAnchorEl] = useState(null);
  const [uploadForm, setUploadForm] = useState(emptyUploadForm);
  const [uploadFileName, setUploadFileName] = useState("");
  const [uploadRows, setUploadRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const isEditing = Boolean(editingFeeBookId);

  const academicYears = useMemo(
    () =>
      [...new Set(feeHeads.map((head) => head.academic_year).filter(Boolean))].sort((a, b) =>
        String(b).localeCompare(String(a))
      ),
    [feeHeads]
  );

  const feeTypes = useMemo(() => {
    const types = new Map();

    feeHeads
      .filter(
        (head) =>
          (!uploadForm.academic_year || head.academic_year === uploadForm.academic_year) &&
          String(head.college_id) === String(collegeId)
      )
      .forEach((head) => {
        if (head.fee_type_id) {
          const feeTypeId = String(head.fee_type_id);

          if (!types.has(feeTypeId)) {
            types.set(feeTypeId, {
              fee_type_id: feeTypeId,
              fee_type_name: head.feeType?.fee_type_name || `Fee Type ${feeTypeId}`,
            });
          }
        }
      });

    return Array.from(types.values()).sort((a, b) =>
      a.fee_type_name.localeCompare(b.fee_type_name)
    );
  }, [feeHeads, uploadForm.academic_year]);

  const uploadFeeHeads = useMemo(
    () =>
      feeHeads.filter(
        (head) =>
          head.academic_year === uploadForm.academic_year &&
          String(head.fee_type_id) === String(uploadForm.fee_type_id) &&
          String(head.status || "").toUpperCase() === "ACTIVE" &&
          String(head.workflow_status || "").toUpperCase() === "APPROVED"
      ),
    [feeHeads, uploadForm.academic_year, uploadForm.fee_type_id]
  );

  const uploadTotalAmount = useMemo(
    () => uploadRows.reduce((total, row) => total + Number(row.fee_amount || 0), 0),
    [uploadRows]
  );

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchFeeBooks = async (gridPage = page, gridPageSize = pageSize, gridSearch = search) => {
    if (!clientId || !collegeId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(gridPage + 1),
        pageSize: String(gridPageSize),
        search: gridSearch,
      });
      const { data } = await axios.get(
        `${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}?${params.toString()}`
      );
      const payload = data?.data || {};
      const items = Array.isArray(payload) ? payload : payload.items || [];
      setRows(items.map(mapFeeBookRow));
      setRowCount(Array.isArray(payload) ? items.length : payload.total || 0);
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Failed to load fee books", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchFeeHeads = async () => {
    if (!clientId || !collegeId) return;
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/fee-heads/${clientId}/${collegeId}`);
      const payload = data?.data || [];
      setFeeHeads(Array.isArray(payload) ? payload : payload.items || []);
    } catch (error) {
      setFeeHeads([]);
      showSnackbar(error.response?.data?.message || "Unable to load fee heads", "error");
    }
  };

  useEffect(() => {
    fetchFeeHeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, collegeId]);

  useEffect(() => {
    fetchFeeBooks(page, pageSize, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, collegeId, page, pageSize, search]);

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingFeeBookId("");
    setForm(emptyForm);
  };

  const handleOpenAdd = () => {
    setEditingFeeBookId("");
    setForm(emptyForm);
    setOpen(true);
  };

  const handleOpenEdit = (row) => {
    const feeBookId = row.fee_book_id || row.id;
    setEditingFeeBookId(feeBookId);
    setForm({
      fee_head_id: row.fee_head_id || "",
      student_id: row.student_id || "",
      fee_amount: row.fee_amount || "",
      workflow_status: row.workflow_status || "DRAFT",
    });
    setOpen(true);
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the fee book entry if it is not linked.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}/${row.fee_book_id}`
      );
      setRows((prevRows) =>
        prevRows.filter((item) => String(item.fee_book_id) !== String(row.fee_book_id))
      );
      setRowCount((prevCount) => Math.max(prevCount - 1, 0));
      await Swal.fire("Deleted", "Fee book deleted successfully.", "success");
    } catch (error) {
      await Swal.fire(
        "Delete failed",
        error.response?.data?.message || "Unable to delete fee book",
        "error"
      );
    }
  };

  const handleSave = async () => {
    if (!form.fee_head_id || !form.student_id || !form.fee_amount) {
      showSnackbar("Fee head, student id and fee amount are required", "warning");
      return;
    }

    const payload = {
      ...form,
      college_id: Number(collegeId),
      client_id: Number(clientId),
      fee_head_id: String(form.fee_head_id),
      student_id: String(form.student_id),
      fee_amount: Number(form.fee_amount),
      created_by: userEmail,
      modified_by: userEmail,
    };

    try {
      let response;
      if (editingFeeBookId) {
        response = await axios.put(
          `${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}/${editingFeeBookId}`,
          payload
        );
        const updatedRow = mapFeeBookRow(response.data?.data || {});
        setRows((prevRows) =>
          prevRows.map((row) =>
            String(row.fee_book_id) === String(editingFeeBookId)
              ? { ...row, ...updatedRow, id: editingFeeBookId }
              : row
          )
        );
        showSnackbar("Fee book updated successfully");
      } else {
        response = await axios.post(
          `${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}`,
          payload
        );
        const createdRow = mapFeeBookRow(response.data?.data || {});
        setRows((prevRows) => [createdRow, ...prevRows].slice(0, pageSize));
        setRowCount((prevCount) => prevCount + 1);
        showSnackbar("Fee book created successfully");
      }
      handleCloseDialog();
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Save failed", "error");
    }
  };

  const closeUploadDialog = () => {
    if (uploading) return;
    setUploadForm(emptyUploadForm);
    setUploadRows([]);
    setUploadFileName("");
    setUploadCompleted(false);
    setUploadResult(null);
  };

  const downloadMissedRollNos = () => {
    const missedRows = uploadRows.filter(
      (row) =>
        row.student_rollno && String(row.validation || "").includes("Student Details are not found")
    );

    if (!missedRows.length) {
      showSnackbar("No missed roll numbers found", "info");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(
      missedRows.map((row) => ({
        student_rollno: row.student_rollno,
        fee_amount: row.fee_amount,
        year_number: row.year_number,
        semester_number: row.semester_number,
        validation: row.validation,
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Missed Roll Nos");
    XLSX.writeFile(workbook, "missed-rollnos.csv", {
      bookType: "csv",
    });
  };

  const downloadDuplicateRollNos = () => {
    const rollNoCounts = new Map();

    uploadRows.forEach((row) => {
      const rollNo = String(row.student_rollno || "")
        .trim()
        .toUpperCase();

      if (rollNo) {
        rollNoCounts.set(rollNo, (rollNoCounts.get(rollNo) || 0) + 1);
      }
    });

    const duplicateRows = uploadRows.filter((row) => {
      const rollNo = String(row.student_rollno || "")
        .trim()
        .toUpperCase();

      return rollNo && rollNoCounts.get(rollNo) > 1;
    });

    if (!duplicateRows.length) {
      showSnackbar("No duplicate roll numbers found", "info");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(
      duplicateRows.map((row) => ({
        student_rollno: row.student_rollno,
        fee_amount: row.fee_amount,
        year_number: row.year_number,
        semester_number: row.semester_number,
        validation: row.validation || "",
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Duplicate Roll Nos");

    XLSX.writeFile(workbook, "duplicate-rollnos.csv", {
      bookType: "csv",
    });
  };

  const handlePreviewFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      if (!worksheet) throw new Error("The selected file has no worksheet");

      const sourceRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
      const previewRows = sourceRows.map((row, index) => {
        const studentRollNo = String(
          getUploadValue(row, ["student_rollno", "student rollno", "rollno", "roll no"])
        ).trim();
        const amountValue = getUploadValue(row, ["fee_amount", "fee amount", "amount"]);
        const feeAmount = Number(amountValue);
        const yearValue = getUploadValue(row, ["year_number", "year number", "year"]);
        const semesterValue = getUploadValue(row, [
          "semester_number",
          "semester number",
          "semester_no",
          "semester no",
          "semester",
        ]);
        const errors = [];
        if (!studentRollNo) errors.push("Roll No is required");
        if (!Number.isFinite(feeAmount) || feeAmount <= 0)
          errors.push("Amount must be greater than zero");
        if (yearValue && (!Number.isInteger(Number(yearValue)) || Number(yearValue) <= 0))
          errors.push("Invalid year");
        if (
          semesterValue &&
          (!Number.isInteger(Number(semesterValue)) || Number(semesterValue) <= 0)
        )
          errors.push("Invalid semester");

        return {
          id: index + 1,
          student_rollno: studentRollNo,
          fee_amount: Number.isFinite(feeAmount) ? feeAmount : 0,
          remarks: String(getUploadValue(row, ["remarks", "remark"]) || "").trim(),
          year_number: yearValue ? Number(yearValue) : "",
          semester_number: semesterValue ? Number(semesterValue) : "",
          validation: errors.join(", "),
        };
      });

      if (!previewRows.length) throw new Error("The selected file has no data rows");

      const duplicateKeys = new Map();
      previewRows.forEach((row) => {
        if (!row.student_rollno) return;
        const key = [
          row.student_rollno.toUpperCase(),
          row.year_number || "",
          row.semester_number || "",
        ].join(":");
        duplicateKeys.set(key, (duplicateKeys.get(key) || 0) + 1);
      });

      const rollNumbers = [
        ...new Set(previewRows.map((row) => row.student_rollno).filter(Boolean)),
      ];
      let validRollNumbers = new Set();
      if (rollNumbers.length) {
        const { data: studentResponse } = await axios.post(
          `${API_BASE_URL}/api/students/by-rollnos`,
          {
            client_id: Number(clientId),
            college_id: Number(collegeId),
            rollnos: rollNumbers,
          }
        );
        validRollNumbers = new Set(
          (studentResponse?.data || []).map((student) =>
            String(student.student_rollno || "")
              .trim()
              .toUpperCase()
          )
        );
      }

      previewRows.forEach((row) => {
        const errors = row.validation ? row.validation.split(", ") : [];
        const normalizedRollNo = row.student_rollno.toUpperCase();
        if (row.student_rollno && !validRollNumbers.has(normalizedRollNo)) {
          errors.push("Student Details are not found");
        }
        const duplicateKey = [
          normalizedRollNo,
          row.year_number || "",
          row.semester_number || "",
        ].join(":");
        if (row.student_rollno && duplicateKeys.get(duplicateKey) > 1) {
          errors.push("Duplicate Roll No/Year/Semester");
        }
        row.validation = errors.join(", ");
      });

      setUploadRows(previewRows);
      setUploadFileName(file.name);
      setUploadCompleted(false);
      setUploadResult(null);
    } catch (error) {
      setUploadRows([]);
      setUploadFileName("");
      setUploadCompleted(false);
      setUploadResult({ severity: "error", message: error.message || "Unable to read the file" });
      showSnackbar(error.message || "Unable to read the selected file", "error");
    } finally {
      event.target.value = "";
    }
  };

  const submitFeeBookUpload = async () => {
    if (!uploadForm.academic_year || !uploadForm.fee_type_id || !uploadForm.fee_head_id) {
      showSnackbar("Select Academic Year, Fee Type and Fee Head", "warning");
      return;
    }
    if (!uploadRows.length) {
      showSnackbar("Select a CSV or Excel file to preview", "warning");
      return;
    }
    const invalidRows = uploadRows.filter((row) => row.validation);
    if (invalidRows.length) {
      showSnackbar(`Correct ${invalidRows.length} invalid row(s) before upload`, "warning");
      return;
    }

    setUploading(true);
    try {
      const normalizedRows = uploadRows.map((row) => ({
        fee_head_id: uploadForm.fee_head_id,
        student_rollno: row.student_rollno,
        fee_amount: row.fee_amount,
        remarks: row.remarks,
        workflow_status: "DRAFT",
        year_number: row.year_number,
        semester_number: row.semester_number,
        fee_status: "notpaid",
        created_by: userEmail,
      }));
      const worksheet = XLSX.utils.json_to_sheet(normalizedRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Fee Books");
      const fileBytes = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const normalizedFile = new File([fileBytes], "fee-book-upload.xlsx", {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const formData = new FormData();
      formData.append("file", normalizedFile);
      formData.append("created_by", userEmail);
      const { data } = await axios.post(
        `${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}/bulk`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      showSnackbar(
        `${data.message || "Fee Books uploaded as Draft"}. Continue in Fee Book Approvals.`
      );
      setUploadRows((currentRows) =>
        currentRows.map((row) => ({ ...row, upload_status: "DRAFT - Uploaded" }))
      );
      setUploadCompleted(true);
      setUploadResult({
        severity: data.failed ? "warning" : "success",
        message: `${data.message || "Fee Books uploaded as Draft"}. ${
          data.failed
            ? `${data.failed} row(s) failed. Review the server response before approval.`
            : "All displayed rows are ready in Fee Book Approvals."
        }`,
      });
      setUploading(false);
      await fetchFeeBooks(page, pageSize, search);
    } catch (error) {
      setUploadResult({
        severity: "error",
        message: error.response?.data?.message || "Upload failed",
      });
      showSnackbar(error.response?.data?.message || "Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCsv = () => {
    const link = document.createElement("a");
    link.href = `${process.env.PUBLIC_URL}/feebook.csv`;
    link.setAttribute("download", "feebook.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadUploadTemplate = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([
      ["student_rollno", "fee_amount", "remarks", "year_number", "semester_number"],
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Fee Book Upload");
    XLSX.writeFile(workbook, "fee-book-upload-template.xlsx");
  };

  const columns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getRowIndex(params.id) + 1,
    },
    { field: "fee_head_name", headerName: "Fee Head", flex: 1.4, minWidth: 260 },
    { field: "student_rollno", headerName: "Roll No", flex: 0.8, minWidth: 120 },
    { field: "student_id", headerName: "Student ID", flex: 0.8, minWidth: 120 },
    { field: "fee_amount", headerName: "Amount", flex: 0.7, minWidth: 110 },
    { field: "balance", headerName: "Balance", flex: 0.7, minWidth: 110 },
    { field: "workflow_status", headerName: "Status", flex: 0.8, minWidth: 120 },
    { field: "created_by", headerName: "Created By", flex: 1, minWidth: 140 },
    { field: "created_on", headerName: "Created On", flex: 0.8, minWidth: 120 },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      minWidth: 130,
      renderCell: (params) => (
        <ActionsMenu row={params.row} onEdit={handleOpenEdit} onDelete={handleDelete} />
      ),
    },
  ];

  const uploadColumns = [
    { field: "id", headerName: "S. No.", width: 75 },
    { field: "student_rollno", headerName: "Roll No", minWidth: 160, flex: 1 },
    {
      field: "fee_amount",
      headerName: "Amount",
      minWidth: 120,
      valueFormatter: ({ value }) => Number(value || 0).toFixed(2),
    },
    { field: "remarks", headerName: "Remarks", minWidth: 190, flex: 1.2 },
    { field: "year_number", headerName: "Year", width: 90 },
    { field: "semester_number", headerName: "Semester", width: 105 },
    {
      field: "upload_status",
      headerName: "Upload Status",
      minWidth: 145,
      valueGetter: (params) => params.row.upload_status || "Not uploaded",
    },
    {
      field: "validation",
      headerName: "Validation",
      minWidth: 220,
      flex: 1.2,
      renderCell: ({ value }) => (
        <Typography variant="caption" color={value ? "error" : "success.main"}>
          {value || "Ready"}
        </Typography>
      ),
    },
  ];

  return (
    <Box p={2}>
      {!openUploadOnMount && (
        <Paper elevation={3} sx={{ p: 2 }}>
          <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
            <Typography variant="h5" sx={{ flexGrow: 1 }}>
              Fee Book
            </Typography>
            <TextField
              size="medium"
              placeholder="Search..."
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 240 }}
            />
            <Button
              variant="contained"
              startIcon={<MoreVertIcon />}
              sx={{ color: "#fff" }}
              onClick={(event) => setAnchorEl(event.currentTarget)}
            >
              Actions
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem disableRipple>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#115293" },
                  }}
                  onClick={() => {
                    setAnchorEl(null);
                    handleOpenAdd();
                  }}
                >
                  ADD NEW FEE BOOK
                </Button>
              </MenuItem>
              <MenuItem disableRipple>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  sx={{
                    backgroundColor: "#4caf50",
                    color: "#fff",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#357a38" },
                  }}
                  onClick={() => {
                    setAnchorEl(null);
                    downloadSampleCsv();
                  }}
                >
                  DOWNLOAD CSV
                </Button>
              </MenuItem>
            </Menu>
          </Toolbar>

          <Box
            mt={2}
            sx={{
              height: "calc(100vh - 230px)",
              minHeight: 420,
              width: "100%",
              overflow: "hidden",
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              page={page}
              pageSize={pageSize}
              rowCount={rowCount}
              pagination
              paginationMode="server"
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(0);
              }}
              rowsPerPageOptions={pageSizeOptions}
              disableSelectionOnClick
              sx={{
                border: "1px solid #e0e0e0",
                "& .MuiDataGrid-cell": {
                  alignItems: "center",
                },
              }}
            />
          </Box>
        </Paper>
      )}

      {openUploadOnMount && (
        <Paper elevation={3} sx={{ p: 2 }}>
          <Toolbar sx={{ px: { xs: 0, sm: 1 } }}>
            <Typography variant="h5" fontWeight="bold">
              UPLOAD FEE BOOK
            </Typography>
          </Toolbar>
          <Box px={{ xs: 0, sm: 1 }} pb={1}>
            <Stack spacing={2} mt={1}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  required
                  select
                  label="Academic Year"
                  value={uploadForm.academic_year}
                  disabled={uploadCompleted}
                  onChange={(event) =>
                    setUploadForm({
                      academic_year: event.target.value,
                      fee_type_id: "",
                      fee_head_id: "",
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ native: true, displayEmpty: true }}
                >
                  <option value="">Select Academic Year</option>
                  {academicYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </TextField>
                <TextField
                  required
                  select
                  label="Fee Type"
                  value={uploadForm.fee_type_id}
                  disabled={uploadCompleted || !uploadForm.academic_year}
                  onChange={(event) =>
                    setUploadForm((current) => ({
                      ...current,
                      fee_type_id: event.target.value,
                      fee_head_id: "",
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ native: true, displayEmpty: true }}
                >
                  <option value="">Select Fee Type</option>
                  {feeTypes.map((feeType) => (
                    <option key={feeType.fee_type_id} value={feeType.fee_type_id}>
                      {feeType.fee_type_name}
                    </option>
                  ))}
                </TextField>
                <TextField
                  required
                  select
                  label="Fee Head"
                  value={uploadForm.fee_head_id}
                  disabled={uploadCompleted || !uploadForm.fee_type_id}
                  onChange={(event) =>
                    setUploadForm((current) => ({
                      ...current,
                      fee_head_id: event.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ native: true, displayEmpty: true }}
                >
                  <option value="">Select Approved Fee Head</option>
                  {uploadFeeHeads.map((feeHead) => (
                    <option key={feeHead.fee_head_id} value={feeHead.fee_head_id}>
                      {feeHead.fee_head_name}
                    </option>
                  ))}
                </TextField>
              </Box>

              {uploadForm.fee_type_id && !uploadFeeHeads.length && (
                <Alert severity="info">
                  No active, approved Fee Heads are available for this Academic Year and Fee Type.
                </Alert>
              )}

              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={downloadUploadTemplate}
                  sx={{
                    bgcolor: "#2e7d32",
                    color: "#fff",
                    "&:hover": { bgcolor: "#1b5e20" },
                  }}
                >
                  Download Template
                </Button>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  disabled={uploadCompleted || !uploadForm.fee_head_id}
                  sx={{
                    color: "#1976d2",
                    borderColor: "#1976d2",
                    "&.Mui-disabled": { borderColor: "#b0bec5", color: "#90a4ae" },
                  }}
                >
                  Select CSV / Excel
                  <input hidden type="file" accept=".csv,.xlsx" onChange={handlePreviewFile} />
                </Button>
                <Typography
                  variant="body2"
                  color={uploadFileName ? "success.main" : "text.secondary"}
                >
                  {uploadFileName ? `Selected: ${uploadFileName}` : "No file selected"}
                </Typography>
              </Box>

              {uploadResult && (
                <Alert severity={uploadResult.severity}>{uploadResult.message}</Alert>
              )}

              {uploadRows.length > 0 && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                      p: 1.5,
                      bgcolor: "#f5f9ff",
                      borderRadius: 1,
                    }}
                  >
                    <Typography fontWeight="bold">Rows: {uploadRows.length}</Typography>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      Total Amount: ₹
                      {uploadTotalAmount.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>

                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={downloadMissedRollNos}
                      sx={{
                        bgcolor: "#ed6c02",
                        color: "#fff",
                        "&:hover": { bgcolor: "#e65100" },
                      }}
                    >
                      Download Missed Roll Nos
                    </Button>

                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={downloadDuplicateRollNos}
                      sx={{
                        bgcolor: "#d32f2f",
                        color: "#fff",
                        "&:hover": { bgcolor: "#b71c1c" },
                      }}
                    >
                      Download Duplicate Roll Nos
                    </Button>
                  </Box>
                  <Box sx={{ height: 360, width: "100%" }}>
                    <DataGrid
                      rows={uploadRows}
                      columns={uploadColumns}
                      pagination
                      page={page}
                      pageSize={pageSize}
                      onPageChange={(newPage) => setPage(newPage)}
                      onPageSizeChange={(newPageSize) => {
                        setPageSize(newPageSize);
                        setPage(0);
                      }}
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      disableSelectionOnClick
                    />
                  </Box>
                  {uploadRows.some((row) => row.validation) && (
                    <Alert severity="warning">
                      Upload is disabled because one or more rows have validation errors. Correct
                      the highlighted rows and select the file again.
                    </Alert>
                  )}
                </>
              )}
            </Stack>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: 1, pt: 1 }}>
            <Button
              variant="contained"
              onClick={closeUploadDialog}
              disabled={uploading}
              sx={{
                bgcolor: "#d32f2f",
                color: "#fff",
                minWidth: 100,
                "&:hover": { bgcolor: "#b71c1c" },
                "&.Mui-disabled": { bgcolor: "#ef9a9a", color: "#fff" },
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              onClick={submitFeeBookUpload}
              disabled={
                uploading ||
                uploadCompleted ||
                !uploadForm.fee_head_id ||
                !uploadRows.length ||
                uploadRows.some((row) => row.validation)
              }
              sx={{
                bgcolor: "#1976d2",
                color: "#fff",
                minWidth: 160,
                "&:hover": { bgcolor: "#115293" },
                "&.Mui-disabled": {
                  color: "#fff",
                  backgroundColor: "#90a4ae",
                  opacity: 1,
                },
              }}
            >
              {uploading
                ? "Uploading..."
                : uploadCompleted
                ? "Uploaded as Draft"
                : "Upload as Draft"}
            </Button>
          </Box>
        </Paper>
      )}

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? "Edit Fee Book" : "Add Fee Book"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Fee Head"
              select
              value={form.fee_head_id}
              onChange={(event) => setForm({ ...form, fee_head_id: event.target.value })}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
            >
              <option value="">Select Fee Head</option>
              {feeHeads.map((feeHead) => (
                <option key={feeHead.fee_head_id} value={feeHead.fee_head_id}>
                  {feeHead.fee_head_name}
                </option>
              ))}
            </TextField>
            <TextField
              label="Student ID"
              value={form.student_id}
              onChange={(event) => setForm({ ...form, student_id: event.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Fee Amount"
              type="number"
              value={form.fee_amount}
              onChange={(event) => setForm({ ...form, fee_amount: event.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Status"
              select
              value={form.workflow_status}
              onChange={(event) => setForm({ ...form, workflow_status: event.target.value })}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
            >
              {workflowStatusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleCloseDialog}
            sx={{
              color: "text.primary",
              borderColor: "divider",
              "&:hover": {
                borderColor: "text.secondary",
                backgroundColor: "action.hover",
              },
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} sx={{ color: "#fff" }}>
            {isEditing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

FeeBook.propTypes = {
  openUploadOnMount: PropTypes.bool,
};
